import { useEffect, useRef, useState } from "react";

import { useClickOutside } from "../../../lib/useClickOutside";
import { btn, input } from "./common";

export type AgentOption = { id: string; name: string; connection: string | null };

/** Lista de agentes com checkbox (busca quando a lista é longa). */
export function AgentChecklist({ options, value, onChange, max }: {
  options: AgentOption[]; value: string[]; onChange: (ids: string[]) => void; max: number;
}) {
  const [q, setQ] = useState("");
  const term = q.trim().toLowerCase();
  const shown = term
    ? options.filter((o) => `${o.name} ${o.connection ?? ""}`.toLowerCase().includes(term))
    : options;
  const full = value.length >= max;

  function toggle(id: string) {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {options.length > 8 && (
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar agente ou conexão…" style={{ ...input, width: "100%" }} />
      )}
      <div style={{ maxHeight: 240, overflowY: "auto", display: "flex", flexDirection: "column" }}>
        {shown.length === 0 && (
          <span style={{ fontSize: 12, color: "var(--ink-dim)", padding: "6px 2px" }}>
            {options.length === 0 ? "Nenhum agente com conexão vinculada." : "Nada encontrado."}
          </span>
        )}
        {shown.map((o) => {
          const checked = value.includes(o.id);
          const blocked = !checked && full;
          return (
            <label key={o.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 4px", borderRadius: 6, fontSize: 13, color: blocked ? "var(--ink-dim)" : "var(--ink)", cursor: blocked ? "default" : "pointer" }}>
              <input type="checkbox" checked={checked} disabled={blocked} onChange={() => toggle(o.id)} />
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {o.name}
                <span style={{ color: o.connection ? "var(--ink-muted)" : "var(--danger)", fontSize: 12 }}>
                  {" "}— {o.connection ?? "sem conexão"}
                </span>
              </span>
            </label>
          );
        })}
      </div>
      <span style={{ fontSize: 11, color: "var(--ink-dim)" }}>
        {value.length} de no máximo {max} selecionados
      </span>
    </div>
  );
}

/** Botão + popover com a checklist (fecha ao clicar fora ou Esc). */
export function AgentPicker(props: {
  options: AgentOption[]; value: string[]; onChange: (ids: string[]) => void; max: number;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, open, () => setOpen(false));
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const n = props.value.length;
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)} style={{ ...btn, minWidth: 220, textAlign: "left", display: "flex", justifyContent: "space-between", gap: 8 }}>
        <span style={{ color: n ? "var(--ink)" : "var(--ink-muted)" }}>
          {n === 0 ? "Selecione…" : n === 1 ? "1 agente" : `${n} agentes`}
        </span>
        <span aria-hidden style={{ color: "var(--ink-muted)" }}>▾</span>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: "var(--z-dropdown)" as unknown as number, width: 360, background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 10, boxShadow: "var(--shadow-pop)", padding: 10 }}>
          <AgentChecklist {...props} />
        </div>
      )}
    </div>
  );
}
